import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Mail, Copy, Check, Clock, X, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * PendingManualInvitesStrip
 * ─────────────────────────
 * V22.9 — WebAdmin Overview audit-grade follow-up queue for invites
 * that were generated in `delivery="manual"` mode (i.e. SendGrid not
 * yet wired). Each row carries:
 *   • Recipient name + role + org
 *   • Time since invite + expiry
 *   • One-click "Copy link" (puts the activation URL on clipboard)
 *   • "Mark as delivered" (calls /auth/invitations/{id}/mark-delivered
 *     to dismiss the row from the strip)
 *
 * Auto-hides when there are no pending manual invites (clean dashboard
 * the moment SendGrid is wired and live email delivery resumes).
 */
function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function PendingManualInvitesStrip() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null); // invite id while updating
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/invitations/manual-pending");
      setRows(r.data?.invitations || []);
    } catch {
      // silent on the dashboard — strip simply won't render
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const copyLink = async (row) => {
    if (!row.accept_url) {
      toast.error("No activation URL stored on this invite");
      return;
    }
    const fullUrl = row.accept_url.startsWith("http")
      ? row.accept_url
      : `${window.location.origin}${row.accept_url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedId(row.id);
      toast.success(`Copied activation link for ${row.email}`);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("Clipboard blocked by browser. Long-press the link to copy.");
    }
  };

  const markDelivered = async (row) => {
    setMarking(row.id);
    try {
      await api.post(`/auth/invitations/${row.id}/mark-delivered`);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      toast.success(`Marked ${row.email} as hand-delivered`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark delivered");
    } finally {
      setMarking(null);
    }
  };

  // Auto-hide once SendGrid is wired (no manual rows in last 24 h).
  if (!loading && rows.length === 0) return null;

  return (
    <Card className="border-amber-200" data-testid="pending-manual-invites-strip">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="p-1.5 rounded-lg bg-amber-100">
              <Mail className="w-4 h-4 text-amber-700" />
            </span>
            Pending Invites &mdash; Manual Mode
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[0.65rem] ml-1">
              SendGrid not configured
            </Badge>
            <span className="ml-1 text-sm font-normal text-slate-400">
              ({rows.length} pending hand-delivery &middot; last 24 h)
            </span>
          </CardTitle>
          <button
            onClick={load}
            className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
            data-testid="manual-invites-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div
                key={r.id}
                data-testid={`manual-invite-row-${r.id}`}
                className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
              >
                <div className="w-8 h-8 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700 text-[0.65rem] font-bold flex-shrink-0">
                  {(r.first_name?.[0] || r.email?.[0] || "?").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">
                    <span className="font-semibold">{r.first_name} {r.last_name}</span>
                    <span className="text-slate-500"> &middot; {(r.role || "").replace(/_/g, " ")}</span>
                    {r.organization_name && (
                      <span className="text-slate-400"> &middot; {r.organization_name}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="truncate">{r.email}</span>
                    <span className="text-slate-300">|</span>
                    <Clock className="w-3 h-3 inline" />
                    <span>invited {timeAgo(r.invited_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyLink(r)}
                    className="text-xs"
                    data-testid={`manual-invite-copy-${r.id}`}
                  >
                    {copiedId === r.id
                      ? <><Check className="w-3 h-3 mr-1 text-emerald-600" /> Copied</>
                      : <><Copy className="w-3 h-3 mr-1" /> Copy link</>}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => markDelivered(r)}
                    disabled={marking === r.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`manual-invite-delivered-${r.id}`}
                  >
                    {marking === r.id
                      ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> …</>
                      : <><X className="w-3 h-3 mr-1" /> Mark delivered</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
